import React, { useState } from 'react';
import { X, Plus, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Note {
  id: string;
  type: 'system' | 'user';
  author?: string;
  timestamp: string;
  title: string;
  content: string;
}
interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  notes: Note[];
}
export function NotesModal({
  isOpen,
  onClose,
  companyName,
  notes
}: NotesModalProps) {
  const [filter, setFilter] = useState<'all' | 'system' | 'user'>('all');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const filteredNotes = notes.filter(note => {
    if (filter === 'all') return true;
    return note.type === filter;
  });
  const handleAddNote = () => {
    // In a real app, this would save to backend
    setNewNoteContent('');
    setShowAddNote(false);
  };
  return <AnimatePresence data-id="element-2772" data-mp="2y8bfgvbtgx1o">
      {isOpen && <>
          {/* No backdrop - drawer is part of the interface */}

          {/* Drawer - Slide in from right edge */}
          <div className="fixed top-0 right-0 bottom-0 z-50 pointer-events-none" data-id="element-2773" data-mp="20xn5r9884khk">
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="bg-white shadow-2xl w-[380px] pointer-events-auto border-l border-gray-200 h-full flex flex-col" data-id="element-2774" data-mp="24zrze7yo9h47">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-2775" data-mp="2spmbuof2x3nx">
                <div className="flex items-center gap-2" data-id="element-2776" data-mp="uarll06vojq7">
                  <MessageSquare className="w-5 h-5 text-gray-600" data-id="element-2777" data-mp="ybbh5zbszfa8" />
                  <div data-id="element-2778" data-mp="ybl9htkblyj3">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2779" data-mp="21m7keh7jx5ic">Notes</h2>
                    <p className="text-xs text-gray-500" data-id="element-2780" data-mp="21obmctlw3ufz">{companyName}</p>
                  </div>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2781" data-mp="2amxar5e1m3oo">
                  <X className="w-5 h-5" data-id="element-2782" data-mp="26jqy9g0c7g9r" />
                </button>
              </div>

              {/* Add Note Button */}
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-2783" data-mp="2kiqlcj87bn3t">
                <button onClick={() => setShowAddNote(!showAddNote)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors" data-id="element-2784" data-mp="2flgzfdk0ttkq">
                  <Plus className="w-4 h-4" data-id="element-2785" data-mp="33cmevrm1ujui" />
                  ADD NOTE
                </button>

                {/* Add Note Form */}
                {showAddNote && <div className="mt-4 space-y-3" data-id="element-2786" data-mp="3hyjhmfaywt91">
                    <textarea value={newNoteContent} onChange={e => setNewNoteContent(e.target.value)} placeholder="Enter your note..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" rows={3} data-id="element-2787" data-mp="3hytacanedeif" />
                    <div className="flex gap-2" data-id="element-2788" data-mp="2z6h3pbppnr2h">
                      <button onClick={handleAddNote} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors" data-id="element-2789" data-mp="33z0gi6gkqi68">
                        Save
                      </button>
                      <button onClick={() => {
                  setShowAddNote(false);
                  setNewNoteContent('');
                }} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors" data-id="element-2790" data-mp="2g8o2qeyndr7q">
                        Cancel
                      </button>
                    </div>
                  </div>}
              </div>

              {/* Filter */}
              <div className="px-6 py-3 border-b border-gray-200" data-id="element-2791" data-mp="1zfeshtc3s32m">
                <select value={filter} onChange={e => setFilter(e.target.value as 'all' | 'system' | 'user')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" data-id="element-2792" data-mp="1uh3jtlxjnylz">
                  <option value="all" data-id="element-2793" data-mp="1gj58kpeisiap">All Notes</option>
                  <option value="system" data-id="element-2794" data-mp="3evndrrlo2br5">System Notes</option>
                  <option value="user" data-id="element-2795" data-mp="3av3h0p509a5j">User Notes</option>
                </select>
              </div>

              {/* Notes List */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-2796" data-mp="wo3tq7hgfung">
                {filteredNotes.map(note => <div key={note.id} className={`border-l-4 pl-4 py-2 ${note.type === 'system' ? 'border-gray-300 bg-gray-50' : 'border-indigo-500 bg-indigo-50'} rounded-r-lg`} data-id="element-2797" data-mp="11kudb4sgx8dm">
                    {note.type === 'user' && note.author && <div className="flex items-center gap-2 mb-2" data-id="element-2798" data-mp="dq153tstjzq6">
                        <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-2799" data-mp="imrkeewsuraj">
                          {note.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-2800" data-mp="1y41pb61g2use">
                          {note.author}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-2801" data-mp="30lrn7i28d81">
                          {note.timestamp}
                        </span>
                      </div>}
                    {note.type === 'system' && <div className="flex items-center gap-2 mb-2" data-id="element-2802" data-mp="cysvqpr930sm">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-2803" data-mp="7zygfxshrud9"></div>
                        <span className="text-xs text-gray-500" data-id="element-2804" data-mp="3gfrt21jpclp1">
                          {note.timestamp}
                        </span>
                      </div>}
                    <div className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2805" data-mp="3q5umg2uqk1nw">
                      {note.title}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-2806" data-mp="3le32ib634u6q">
                      {note.content}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}